import React, { useEffect, useRef, useState } from "react";
import { showToaster, toasterTypes } from "..";
import { MESSAGES } from "../../utils/Constants";

export default function AudioCaptureUpload(props: any) {
  const [audio, setAudio] = useState<File | null>(null);     // single audio
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  /* ───────── init microphone once ───────── */
  useEffect(() => {
    if (props.isPreview) return;

    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        setStream(s);
      } catch (e) {
        showToaster(toasterTypes.ERROR, "Couldn't access microphone");
      }
    })();
    return () => stream?.getTracks().forEach(t => t.stop());
  }, [props.isPreview]);

  /* ───────── helpers ───────── */
  const startRecording = () => {
    if (!stream) return;
    
    recordedChunks.current = [];
    const mediaRecorder = new MediaRecorder(stream);
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunks.current, { type: "audio/webm" });
      const audioFile = new File([blob], `recording_${Date.now()}.webm`, { type: "audio/webm" });
      validateAndSave(audioFile);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) validateAndSave(f);
  };

  const validateAndSave = (file: File) => {
    /* ---------- type guard ---------- */
    const allowed = props.acceptFiles?.split("|") ?? ["webm", "mp3", "wav", "m4a", "ogg"];
    const ext = file.type.split("/")[1] || file.name.split('.').pop()?.toLowerCase();
    if (!allowed.includes(ext)) {
      let list = allowed.join(", ").replace(/,([^,]*)$/, " and$1");             // a, b and c
      showToaster(
        toasterTypes.ERROR,
        props.messages?.acceptFiles || MESSAGES.ALLOWED_TYPES.replace("#{value}", list)
      );
      return;
    }

    /* ---------- size guard ---------- */
    if (props.maxSize && file.size > props.maxSize) {
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxSize || MESSAGES.MAX_SIZE.replace("#value", props.maxSize)
      );
      return;
    }

    /* ---------- total‑size guard (single audio) ---------- */
    if (props.maxLimit && file.size > props.maxLimit) {
      const mb = (props.maxLimit / (1024 * 1024)).toFixed(2);
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxLimit || `Combined file size must not exceed ${mb} MB`
      );
      return;
    }
    
    setAudio(file);
    props.onUpload(file);                               // bubble up just like FileUpload
    showToaster(toasterTypes.SUCCESS, "Audio selected!");
  };

  const removeAudio = (e: React.MouseEvent) => {
    setAudio(null);
    props.onUpload(null); // Clear the value in parent component
    e.stopPropagation();
  };

  if (props?.value && props?.isPreview) {
    return (
      <div className="audio-preview">
        <div className="preview-with-delete">
          <audio 
            src={`https://go.coguide.in/c1/download/${props?.value}`} 
            controls 
            preload="metadata"
          />
          <img
            src={`${process.env.PUBLIC_URL}/images/close_circle.svg`}
            onClick={removeAudio}
            alt="remove"
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>
    );
  }
  
  if (!props?.value && props?.isPreview) {
    return (
      <div className="audio-preview">
        <p>No Data</p>
      </div>
    );
  }

  /* ───────── render ───────── */
  return (
    <div className="custom-audio-upload">
      <section className="upload-container">
        {audio ? (
          <div className="preview-with-delete">
            <audio 
              src={URL.createObjectURL(audio)} 
              controls 
              preload="metadata"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/close_circle.svg`}
              onClick={removeAudio}
              alt="remove"
              style={{ cursor: 'pointer' }}
            />
          </div>
        ) : (
          <p>
            <span className="upload-placeholder">{props.placeholder}</span>
            <u className="browse-text" onClick={() => fileInput.current?.click()}>
              Browse Audio
            </u>
          </p>
        )}

        {/* hidden file input for manual pick */}
        <input
          type="file"
          accept="audio/*"
          ref={fileInput}
          style={{ display: "none" }}
          onChange={pickFile}
          disabled={props.isDisabled}
        />

        {/* recording controls */}
        <div className="recording-wrapper">
          <div className={`recording-indicator ${recording ? 'recording' : ''}`}>
            <div className="recording-dot"></div>
            <span className="recording-text">
              {recording ? "Recording..." : "Ready to record"}
            </span>
          </div>
          
          <div className="audio-controls">
            {!recording ? (
              <button 
                disabled={props.isDisabled || !stream} 
                onClick={startRecording}
                className="record-button"
              >
                Start Recording
              </button>
            ) : (
              <button 
                disabled={props.isDisabled} 
                onClick={stopRecording}
                className="record-button recording"
              >
                Stop Recording
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}